import { Button } from "$components/button/button";
import { ErrorState } from "$components/error-state";
import { CheckIcon } from "$components/icons";
import { InfoField } from "$components/info-field";
import { Page } from "$components/page";
import { TextArea } from "$components/rhf-fields";
import { useUser } from "$global-providers/user-provider";
import { formatDate, formatUserName } from "$routes/inventory/_lib/formatting";
import InventoryTitleRow from "$routes/inventory/_lib/inventory-title-row";
import { LocationSelect } from "$routes/inventory/_lib/location-select";
import { linkToInventoryMoveRequests } from "$routes/inventory/links";
import { Box, CircularProgress } from "@material-ui/core";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useFulfillInventoryMoveRequestPageQuery } from "./_lib/__generated__/queries.gql";
import FulfillMoveRequestTable from "./_lib/fulfill-move-request-table";
import useFulfillMoveRequestForm from "./_lib/use-fulfill-move-request-form";

const FulfillMoveRequest: FC = () => {
  const { moveRequestId } = useParams<{ moveRequestId: string }>();
  const { formatMessage } = useIntl();
  const { facilityId } = useUser();
  const [locationId, setLocationId] = useState<string | null>(null);
  const {
    formMethods,
    onSubmit,
    onReject,
    resetSkuValues,
    disableSubmit,
    loading: submitLoading,
  } = useFulfillMoveRequestForm({
    moveRequestId,
  });

  const { data, error, loading } = useFulfillInventoryMoveRequestPageQuery({
    variables: {
      input: {
        id: moveRequestId,
      },
      lotsInput: {
        fulfillingFacilityId: facilityId,
        // Defaulting this here is gross but necessary otherwise the input validation for the inventoryMoveRequest.skus.items.availibleLots will fail
        // Ideally I'd have used the @includes directive that is build into the gql spec to conditionally fetch these details only when we pass a
        // defined 'fulfillingLocationId', however, it seems like the version of ApolloServer we are using doesn't support that.
        // Details of the includes directive: https://graphql.org/learn/queries/#directives
        // Either way from the FEs perspective, this has the same effect.
        fulfillingLocationId:
          locationId || "something so validation does not fail",
      },
    },
    fetchPolicy: "cache-and-network",
    onCompleted: (_data) => {
      resetSkuValues(_data?.inventoryMoveRequest.skus.items);
    },
  });

  function onLocationSelect(_locationId: string | null) {
    setLocationId(_locationId);
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return (
    <Page data-dense>
      <InventoryTitleRow
        backToLink={linkToInventoryMoveRequests()}
        title={
          <FormattedMessage
            id="inventory.requests.fulfill.title"
            defaultMessage="Fulfill Request"
          />
        }
      >
        <Box display="flex" flexWrap="wrap">
          <InfoField
            id="requestedDate"
            label={formatMessage({
              id: "inventory.requests.fulfill.requestedDate",
              defaultMessage: "Requested",
            })}
            value={formatDate(data?.inventoryMoveRequest?.createdAt)}
            width="120px"
          />
          <InfoField
            id="requestedBy"
            label={formatMessage({
              id: "inventory.requests.fulfill.requestedBy",
              defaultMessage: "Requested By",
            })}
            value={formatUserName(data?.inventoryMoveRequest?.requestor)}
            minWidth="200px"
          />
          <InfoField
            id="requestedLocation"
            label={formatMessage({
              id: "inventory.requests.fulfill.requestedLocation",
              defaultMessage: "Location of Request",
            })}
            value={data?.inventoryMoveRequest?.receivingLocation?.name ?? ""}
            minWidth="300px"
          />
        </Box>
      </InventoryTitleRow>
      <form onSubmit={onSubmit}>
        <FormProvider {...formMethods}>
          <Box width="300px">
            <LocationSelect
              id="fulfillingLocationId"
              label={formatMessage({
                id: "inventory.requests.fulfill.fulfillingLocation",
                defaultMessage: "Send From Location",
              })}
              value={locationId}
              onChange={onLocationSelect}
            />
          </Box>
          <FulfillMoveRequestTable
            data={data?.inventoryMoveRequest?.skus?.items}
            loading={loading}
            childrenFetched={!!locationId}
          />
          <Box marginTop={2} marginBottom={2}>
            <TextArea
              name="notes"
              label={formatMessage({
                id: "inventory.requests.fulfill.notes",
                defaultMessage: "Notes",
              })}
            />
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Button color="error" variant="contained" onClick={onReject}>
              <FormattedMessage
                id="inventory.moveRequest.fulfill.reject"
                defaultMessage="Reject Request"
              />
            </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              startIcon={
                submitLoading ? undefined : <CheckIcon color="white" />
              }
              disabled={disableSubmit || submitLoading}
            >
              {submitLoading ? (
                <CircularProgress />
              ) : (
                <FormattedMessage
                  id="inventory.moveRequest.fulfill.submit"
                  defaultMessage="Fulfill Request"
                />
              )}
            </Button>
          </Box>
        </FormProvider>
      </form>
    </Page>
  );
};

export default FulfillMoveRequest;
